import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import axios from "axios";
import moment from "moment";
import qs from "qs";
import { useState, useEffect, useCallback, useRef } from "react";
import { Button, Container, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";

import { EAR_STATUSES } from "api/memberPortal/ear/ear.types";
import AddEarForm from "components/Products/ProductManager/ProductDetailsAccordions/AddEarForm/AddEar.modal";
import ResponsiveMaterialTable from "components/UI/MaterialTable/ResponsiveMaterialTable";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";

import Filter from "../Filter/Filter";

export default function EarReviewsTable({
  refetch,
  filter,
  setEarCount,
  showAddEarButton = false,
}) {
  const earTableRef = useRef();

  const [ERTMember, setERTMember] = useState(false);
  const [filterOpen, setFilterOpen] = useState(true);
  const [showAddEarModal, setShowAddEarModal] = useState(false);
  const [sidebarFilters, setSidebarFilters] = useState({
    status: EAR_STATUSES.PENDING_REVIEW,
  });

  const { authToken, currentUser, permissions } = useAuth();

  useEffect(() => {
    if (earTableRef.current) {
      earTableRef.current.onQueryChange();
    }
  }, [sidebarFilters, refetch]);

  useInterval(() => {
    earTableRef.current.onQueryChange();
  }, 300000); //5 min

  const changeFilter = () => {
    setFilterOpen(!filterOpen);
  };

  const getERTUsers = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}virtual-team/members/is_ert_team_member/`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
        },
      )
      .then((response) => {
        if (response?.data?.is_member === true) {
          setERTMember(true);
        } else {
          setERTMember(false);
        }
      })
      .catch((err) => console.log("unable to get member's virtual teams", err));
  }, []);

  useEffect(() => {
    getERTUsers();
  }, [currentUser.id, getERTUsers]);

  useInterval(() => {
    getERTUsers();
  }, 300000); //5 min

  const columns = [
    {
      title: "ID",
      field: "earid",
      type: "numeric",
      headerStyle: { position: "sticky", left: 0 },
      cellStyle: {
        width: "7%",
        position: "sticky",
        left: 0,
        zIndex: 99,
      },
      render: (rowData) => (
        <Link
          to={`/community/ear-reviews/details/${rowData?.earid}`}
          className="text-decoration-underline"
        >
          {rowData?.earid}
        </Link>
      ),
    },
    {
      title: "VID No.",
      field: "pid.v_id",
      render: (rowData) => <>VID{rowData?.pid?.v_id}</>,
    },
    {
      title: "Status",
      field: "status",
    },
    {
      title: "Agents",
      field: "agents",
      render: (rowData) => (
        <>
          {rowData.agents.map((agent) => (
            <div key={agent.id}>
              - {agent.first_name} {agent.last_name}
            </div>
          ))}
        </>
      ),
    },
    {
      title: "Escalated",
      field: "release_date",
      type: "date",
      render: (rowData) =>
        rowData.release_date
          ? moment.utc(rowData.release_date).format("M/D/YYYY")
          : "TBD",
    },
    {
      title: "Deadline",
      field: "due",
      type: "date",
      render: (rowData) =>
        rowData.due ? moment.utc(rowData.due).format("M/D/YYYY") : "TBD",
    },
    {
      title: "Escalated to ERT",
      hidden: !ERTMember,
      render: (rowData) =>
        rowData?.escalated && <CheckCircleIcon color="danger" />,
    },
    {
      title: "Actions",
      headerStyle: { position: "sticky", right: 0 },
      cellStyle: { position: "sticky", right: 0 },
      render: (rowData) => (
        <Container>
          <Row>
            <Col>
              <Link
                to={`/community/ear-reviews/details/${rowData?.earid}`}
                className="text-decoration-none"
              >
                <Button
                  size="sm"
                  className="rounded-pill small-font pt-0 pb-0 mb-2"
                >
                  View Details
                </Button>
              </Link>
            </Col>
          </Row>
        </Container>
      ),
    },
  ];

  const options = {
    sorting: true,
    search: false,
    columnsButton: true,
    pageSize: 20,
    emptyRowsWhenPaging: false,
  };

  // TODO: change to consts once roles merged in
  const canAddEars = ["NIAP", "Validator"].includes(permissions.role_type);

  return (
    <>
      {filter && (
        <Row className="mt-3 justify-content-end">
          <Col
            sm={12}
            md={2}
            className="d-flex justify-content-end align-content-center mb-2"
          >
            <Button
              variant="link rounded-pill"
              style={{
                backgroundColor: "#dedede",
                maxHeight: 40,
                minWidth: "225px",
              }}
              onClick={changeFilter}
            >
              {filterOpen && (
                <>
                  <KeyboardArrowRightIcon />
                  <KeyboardArrowRightIcon />
                  &nbsp; Close Filter &nbsp;
                  <KeyboardArrowRightIcon />
                  <KeyboardArrowRightIcon />
                </>
              )}
              {!filterOpen && (
                <>
                  <KeyboardArrowLeftIcon />
                  <KeyboardArrowLeftIcon />
                  &nbsp; Open Filter &nbsp;
                  <KeyboardArrowLeftIcon />
                  <KeyboardArrowLeftIcon />
                </>
              )}
            </Button>
          </Col>
        </Row>
      )}
      <Row className="mb-3">
        <Col
          xs={filterOpen && filter ? 2 : 12}
          md={filterOpen && filter ? 9 : 12}
        >
          {showAddEarButton && canAddEars && (
            <Row className="flex-row-reverse m-0 mb-3">
              <Button
                variant="success"
                onClick={() => setShowAddEarModal(true)}
                className="w-auto"
              >
                <AddIcon />
                New EAR
              </Button>
            </Row>
          )}
          <ResponsiveMaterialTable
            id={"ear-reviews-table"}
            title="EAR Reviews"
            columns={columns}
            tableRef={earTableRef}
            data={(query) =>
              new Promise((resolve, reject) => {
                // Extract the necessary information from the query object
                const {
                  page,
                  pageSize,
                  search,
                  filters,
                  orderBy,
                  orderDirection,
                } = query;
                let newFilters = filters.map((filter) => {
                  let value = "";
                  if (Array.isArray(filter.value)) {
                    value = filter.value;
                  } else {
                    value = filter.value.replace(/['"]+/g, "");
                  }
                  return `"${filter.column.field.replace(/\./g, "__")}":"${value}"`;
                });

                const params = {
                  ...sidebarFilters,
                  offset: page * pageSize,
                  limit: pageSize,
                  search: search,
                  filters: newFilters,
                  orderBy: orderBy?.field,
                  orderDirection: orderDirection,
                  earTable: true,
                };

                if (
                  permissions?.role_type === "Lab" ||
                  permissions?.role_permissions?.find(
                    (permission) => permission.area === "EAR Reviews",
                  )?.local_only
                ) {
                  params["local_only"] = true;
                }

                if (permissions?.role_type === "SME") {
                  params["escalated"] = true;
                }

                axios
                  .get(
                    `${import.meta.env.VITE_DJANGO_ENDPOINT}ear/?${qs.stringify(
                      params,
                      {
                        arrayFormat: "comma",
                      },
                    )}`,
                    {
                      withCredentials: true,
                      headers: {
                        Authorization: `Token ${authToken}`,
                      },
                    },
                  )
                  .then((response) => {
                    if (setEarCount) {
                      setEarCount(response.data.count);
                    }
                    resolve({
                      data: response.data.results,
                      page: page,
                      totalCount: response.data.count,
                    });
                  })
                  .catch((error) => {
                    reject(error);
                  });
              })
            }
            options={options}
          />
        </Col>
        {filterOpen && filter && (
          <Col xs={10} md={3}>
            <Filter filters={sidebarFilters} setFilters={setSidebarFilters} />
          </Col>
        )}
      </Row>
      {showAddEarModal && (
        <AddEarForm
          show={showAddEarModal}
          onHide={() => setShowAddEarModal(false)}
          product_id=""
        />
      )}
    </>
  );
}
